import { useState, useEffect, useCallback, useRef } from 'react';
import { pagesAPI, articlesAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import ImagePickerField from '../../components/ui/ImagePickerField';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';

const TABS = [
  { key: 'pages',    label: 'SPA Pages' },
  { key: 'articles', label: 'Articles & News' },
];

// ── Multi-Image Gallery Picker ────────────────────────────
function GalleryPicker({ urls = [], onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput]   = useState('');
  const [showUrl, setShowUrl]     = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const res  = await fetch(`${MEDIA_URL}/api/media/upload`, { method: 'POST', body: fd });
      const data = await res.json();
      const newUrls = (data.data || []).map(f => f.url);
      onChange([...urls, ...newUrls]);
    } catch { alert('Upload failed'); } finally { setUploading(false); }
  };

  const addUrl = () => {
    if (!urlInput.trim() || urls.includes(urlInput.trim())) return;
    onChange([...urls, urlInput.trim()]);
    setUrlInput(''); setShowUrl(false);
  };

  const remove = (url) => onChange(urls.filter(u => u !== url));

  return (
    <div>
      <label style={labelStyle}>Gallery Images (Multiple)</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {urls.map((url, i) => (
          <div key={i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => e.target.style.display = 'none'}/>
            <button onClick={() => remove(url)}
              style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.75)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>
        ))}
        <button onClick={() => fileRef.current.click()}
          style={{ width: 72, height: 72, borderRadius: 6, border: '1.5px dashed var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
          {uploading ? '…' : '+'}
        </button>
      </div>
      <input ref={fileRef} type="file" multiple accept="image/*" style={{ display: 'none' }} onChange={upload}/>
      {showUrl ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={urlInput} onChange={e => setUrlInput(e.target.value)}
            placeholder="Paste image URL..."
            style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '6px 10px', fontSize: 12 }}/>
          <Button size="sm" onClick={addUrl}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => setShowUrl(false)}>✕</Button>
        </div>
      ) : (
        <button onClick={() => setShowUrl(true)}
          style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          + Add from URL instead
        </button>
      )}
    </div>
  );
}

export default function ContentPage() {
  const [tab, setTab]           = useState('pages');
  const [pages, setPages]       = useState([]);
  const [articles, setArticles] = useState([]);
  const [pagination, setPag]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [modal, setModal]       = useState(null);
  const [selected, setSel]      = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({});

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pagesAPI.list({ page });
      setPages(res.data.data || []); setPag(res.data.pagination);
    } catch { setPages([]); } finally { setLoading(false); }
  }, [page]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await articlesAPI.list({ page });
      setArticles(res.data.data || []); setPag(res.data.pagination);
    } catch { setArticles([]); } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { tab === 'pages' ? loadPages() : loadArticles(); }, [tab, loadPages, loadArticles]);
  useEffect(() => { setPage(1); }, [tab]);

  const openCreatePage    = () => { setForm({ title: '', slug: '', is_public: true, order_index: pages.length + 1 }); setSel(null); setModal('page'); };
  const openCreateArticle = () => { setForm({ title: '', slug: '', content: '', excerpt: '', content_type: 'media', status: 'draft', tags: '', media_url: '', video_url: '', media_urls: [], is_featured: false }); setSel(null); setModal('article'); };
  const openEditPage      = (p) => { setForm({ title: p.title, slug: p.slug, is_public: p.is_public, order_index: p.order_index }); setSel(p); setModal('page'); };
  const openEditArticle   = (a) => {
    setForm({
      title:        a.title,
      slug:         a.slug,
      content:      a.content || '',
      excerpt:      a.excerpt || '',
      content_type: a.content_type,
      status:       a.status,
      tags:         (a.tags || []).join(', '),
      media_url:    a.media_url || '',
      video_url:    a.video_url || '',
      media_urls:   a.media_urls || [],
      is_featured:  a.is_featured || false,
    });
    setSel(a); setModal('article');
  };

  const handlePublish = async (id) => { await articlesAPI.publish(id); loadArticles(); };

  const handleSavePage = async () => {
    setSaving(true);
    try {
      if (!selected) await pagesAPI.create(form);
      else await pagesAPI.update(selected.id, form);
      setModal(null); loadPages();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleSaveArticle = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags:       form.tags.split(',').map(t => t.trim()).filter(Boolean),
        media_urls: form.media_urls || [],
      };
      if (!selected) await articlesAPI.create(payload);
      else           await articlesAPI.update(selected.id, payload);
      setModal(null); loadArticles();
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const PAGE_COLS = [
    { key: 'slug',        label: 'Route',      render: v => <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--blue)' }}>{v}</span> },
    { key: 'title',       label: 'Title',      render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'order_index', label: 'Order',      render: v => <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>#{v}</span> },
    { key: 'is_public',   label: 'Visibility', render: v => <Badge status={v ? 'active' : 'inactive'} label={v ? 'Public' : 'Hidden'} /> },
    { key: 'updated_at',  label: 'Updated',    render: v => new Date(v).toLocaleDateString() },
  ];

  const ART_COLS = [
    { key: 'cover_image_url', label: '', render: (v, row) => {
      const src = v || row.media_url;
      return (
        <div style={{ width: 36, height: 36, borderRadius: 6, overflow: 'hidden', background: 'var(--bg-elevated)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : <span style={{ fontSize: 14, opacity: 0.3, color: 'var(--accent)' }}>◈</span>}
        </div>
      );
    }},
    { key: 'title', label: 'Article', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {row.author_name || '—'} · {row.content_type}
          {row.media_urls?.length > 0 && <span style={{ marginLeft: 6, color: 'var(--accent)' }}>🖼 {row.media_urls.length} images</span>}
        </div>
      </div>
    )},
    { key: 'status',      label: 'Status',   render: v => <Badge status={v} /> },
    { key: 'is_featured', label: 'Featured', render: v => v ? <span style={{ color: 'var(--accent)' }}>★</span> : '—' },
    { key: 'published_at', label: 'Published', render: v => v ? new Date(v).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>Draft</span> },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        {row.status !== 'published' && <Button variant="secondary" size="sm" onClick={() => handlePublish(row.id)}>Publish</Button>}
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, marginBottom: -1, transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => <button key={t.key} onClick={() => setTab(t.key)} style={tabBtn(t.key)}>{t.label}</button>)}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pagination?.total ?? 0} records</span>
        <Button onClick={tab === 'pages' ? openCreatePage : openCreateArticle}>
          + New {tab === 'pages' ? 'Page' : 'Article'}
        </Button>
      </div>

      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {tab === 'pages'
          ? <Table columns={PAGE_COLS} data={pages}    loading={loading} onRowClick={openEditPage} />
          : <Table columns={ART_COLS}  data={articles} loading={loading} onRowClick={openEditArticle} />}
        <Pagination pagination={pagination} onPage={setPage} />
      </div>

      {/* Page modal */}
      <Modal open={modal === 'page'} onClose={() => setModal(null)} title={selected ? `Edit — ${selected.title}` : 'New Page'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Page Title" value={form.title || ''} onChange={e => {
            const slug = '/' + e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            setForm(f => ({ ...f, title: e.target.value, slug }));
          }} />
          <Input label="URL Slug" value={form.slug || ''} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} style={{ fontFamily: 'monospace' }} />
          <div style={{ display: 'flex', gap: 14 }}>
            <Input label="Nav Order" type="number" value={form.order_index || ''} style={{ width: 100 }} onChange={e => setForm(f => ({ ...f, order_index: parseInt(e.target.value) }))} />
            <Select label="Visibility" value={form.is_public ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, is_public: e.target.value === 'true' }))}>
              <option value="true">Public</option>
              <option value="false">Hidden</option>
            </Select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSavePage} loading={saving}>{selected ? 'Save' : 'Create Page'}</Button>
          </div>
        </div>
      </Modal>

      {/* Article modal */}
      <Modal open={modal === 'article'} onClose={() => setModal(null)} title={selected ? 'Edit Article' : 'New Article'} width={740}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Cover + Video */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <ImagePickerField
              label="Cover / Hero Image"
              value={form.media_url || ''}
              onChange={url => setForm(f => ({ ...f, media_url: url }))}
            />
            <ImagePickerField
              label="Article Video (optional)"
              value={form.video_url || ''}
              onChange={url => setForm(f => ({ ...f, video_url: url }))}
              accept="video"
            />
          </div>

          {/* Gallery images */}
          <GalleryPicker
            urls={form.media_urls || []}
            onChange={urls => setForm(f => ({ ...f, media_urls: urls }))}
          />

          <Input label="Title" value={form.title || ''} onChange={e => {
            const slug = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            setForm(f => ({ ...f, title: e.target.value, slug }));
          }} />

          <Input label="URL Slug" value={form.slug || ''} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} style={{ fontFamily: 'monospace' }} />
          <Input label="Excerpt (shown in list & cards)" value={form.excerpt || ''} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Select label="Content Type" value={form.content_type || 'media'} onChange={e => setForm(f => ({ ...f, content_type: e.target.value }))}>
              <option value="media">Media / News</option>
              <option value="corporate">Corporate</option>
              <option value="service">Service</option>
              <option value="program">Program</option>
              <option value="marketing">Marketing</option>
              <option value="system">System</option>
            </Select>
            <Select label="Status" value={form.status || 'draft'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}>
                <input type="checkbox" checked={form.is_featured || false} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))}/>
                <span style={{ color: 'var(--text-secondary)' }}>★ Featured Article</span>
              </label>
            </div>
          </div>

          <Input label="Tags (comma-separated)" value={form.tags || ''} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="youth, technology, rwanda" />

          <div>
            <label style={labelStyle}>Content (supports HTML)</label>
            <textarea value={form.content || ''} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={10}
              style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '10px 12px', fontSize: 13, resize: 'vertical', lineHeight: 1.7, fontFamily: 'monospace', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Tip: Use &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;strong&gt;, &lt;blockquote&gt; for rich formatting
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveArticle} loading={saving}>{selected ? 'Save Changes' : 'Create Article'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = {
  fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.03em',
  display: 'block', marginBottom: 6,
};